import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { alpha } from '@mui/material/styles';
import { CONFIRMATION_TEXT } from '../data/accountDeletionData';

export type DeleteAccountModalProps = {
  open: boolean;
  description: string;
  confirmationInput: string;
  isRequesting: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onDescriptionChange: (value: string) => void;
  onConfirmationInputChange: (value: string) => void;
  onConfirm: () => void;
};

export function DeleteAccountModal({
  open,
  description,
  confirmationInput,
  isRequesting,
  canSubmit,
  onClose,
  onDescriptionChange,
  onConfirmationInputChange,
  onConfirm,
}: DeleteAccountModalProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isConfirmationValid = confirmationInput.trim().toLowerCase() === CONFIRMATION_TEXT;

  return (
    <Dialog
      open={open}
      onClose={isRequesting ? undefined : onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          overflow: 'hidden',
          boxShadow: isMobile ? 'none' : '0 16px 48px rgba(15, 23, 42, 0.16)',
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: (muiTheme) => alpha(muiTheme.palette.error.main, 0.04),
        }}
      >
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, minWidth: 0 }}>
          <Box
            sx={{
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: (muiTheme) => alpha(muiTheme.palette.error.main, 0.12),
              color: 'error.main',
              '& .MuiSvgIcon-root': { fontSize: { xs: 18, sm: 20 } },
            }}
          >
            <DeleteOutlineIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.125rem' }, lineHeight: 1.3 }}>
              Confirm Account Deletion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Permanent removal after the 7-day grace period
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={isRequesting}
          size="small"
          sx={{ mt: -0.25, mr: -0.5, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          '&.MuiDialogContent-root': { paddingTop: { xs: 1.5, sm: 2 } },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 2 }, lineHeight: 1.5, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
          Your account will be scheduled for deletion once confirmed. You may cancel during the 7-day grace
          period before removal becomes permanent.
        </Typography>

        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'text.secondary',
              mb: 0.75,
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
            }}
          >
            Feedback (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={isMobile ? 2 : 3}
            placeholder="Share why you're leaving"
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value.slice(0, 500))}
            inputProps={{ maxLength: 500 }}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: 'background.default',
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
            {description.length}/500
          </Typography>
        </Box>

        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: '8px',
            border: '1px solid',
            borderColor: (muiTheme) =>
              isConfirmationValid ? alpha(muiTheme.palette.error.main, 0.4) : 'divider',
            bgcolor: (muiTheme) => alpha(muiTheme.palette.error.main, 0.03),
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'text.secondary',
              mb: 0.75,
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
            }}
          >
            Verification required
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.5, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            Type{' '}
            <Box component="span" sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'error.main' }}>
              {CONFIRMATION_TEXT}
            </Box>{' '}
            below to confirm.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder={CONFIRMATION_TEXT}
            value={confirmationInput}
            onChange={(event) => onConfirmationInputChange(event.target.value)}
            error={confirmationInput.length > 0 && !isConfirmationValid}
            helperText={
              confirmationInput.length > 0 && !isConfirmationValid
                ? `Enter "${CONFIRMATION_TEXT}" exactly to continue`
                : undefined
            }
            FormHelperTextProps={{ sx: { mx: 0, mt: 0.5 } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: 'background.paper',
                fontFamily: 'monospace',
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
        }}
      >
        <Button
          onClick={onClose}
          disabled={isRequesting}
          variant="outlined"
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'medium'}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canSubmit}
          onClick={onConfirm}
          fullWidth={isMobile}
        >
          {isRequesting ? <CircularProgress size={22} color="inherit" /> : 'Delete Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
